import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Mail, ExternalLink, Trash2 } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { adminListLeads, adminUpdateLead, adminDeleteLead } from '@/lib/api';
import { ADMIN_LEADS } from '@/constants/testIds';

const STATUSES = ['new', 'contacted', 'booked', 'archived'];

const STATUS_STYLE = {
  new: 'bg-[hsl(var(--lxra-gold))]/15 text-[hsl(var(--lxra-gold-hover))] border-[hsl(var(--lxra-gold))]/30',
  contacted: 'bg-[#141416] text-[hsl(var(--lxra-fg))] border-[hsl(var(--lxra-border))]',
  booked: 'bg-[hsl(152,46%,25%)]/30 text-[hsl(152,46%,65%)] border-[hsl(152,46%,35%)]/40',
  archived: 'bg-transparent text-[hsl(var(--lxra-muted))] border-[hsl(var(--lxra-border))]',
};

const selectClass = 'h-9 bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] text-xs focus:ring-[hsl(var(--lxra-gold))]';
const contentClass = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]';

export const AdminLeadsTab = () => {
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState('all');

  const refresh = useCallback(() => {
    const params = filter === 'all' ? {} : { status: filter };
    return adminListLeads(params).then(setLeads).catch(() => setLeads([]));
  }, [filter]);

  useEffect(() => {
    setLeads(null);
    refresh();
  }, [refresh]);

  const updateStatus = async (id, status) => {
    try {
      const updated = await adminUpdateLead(id, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? updated : l)));
      toast.success(`Lead marked ${status}.`);
    } catch {
      toast.error('Could not update lead.');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this lead?')) return;
    try {
      await adminDeleteLead(id);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success('Lead removed.');
    } catch {
      toast.error('Could not remove lead.');
    }
  };

  const counts = leads ? STATUSES.reduce((acc, s) => ({ ...acc, [s]: leads.filter((l) => l.status === s).length }), {}) : {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">LXRA Creator</p>
          <h2 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">Leads</h2>
          <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">Every Session Planner submission and contact message, in one pipeline.</p>
        </div>
        <div className="flex items-center gap-3">
          {filter === 'all' && leads && (
            <span className="hidden sm:inline text-xs text-[hsl(var(--lxra-muted))]">
              {counts.new || 0} new · {counts.contacted || 0} contacted · {counts.booked || 0} booked
            </span>
          )}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className={`${selectClass} w-40`} data-testid={ADMIN_LEADS.statusFilter}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!leads ? (
        <Skeleton className="h-64 rounded-2xl bg-[#141416]" />
      ) : (
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden" data-testid={ADMIN_LEADS.table}>
          <Table>
            <TableHeader>
              <TableRow className="border-[hsl(var(--lxra-border))] hover:bg-transparent">
                <TableHead className="text-[hsl(var(--lxra-muted))]">Source</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Lead</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Occasion</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Service</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Status</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Received</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.length === 0 && (
                <TableRow className="border-[hsl(var(--lxra-border))]">
                  <TableCell colSpan={7} className="text-center text-[hsl(var(--lxra-muted))] py-10">No leads match this filter.</TableCell>
                </TableRow>
              )}
              {leads.map((l) => (
                <TableRow key={l.id} className="border-[hsl(var(--lxra-border))] hover:bg-[#141416]" data-testid={ADMIN_LEADS.row(l.id)}>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--lxra-muted))] capitalize">
                      {l.source === 'planner' ? <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> : <Mail className="h-3.5 w-3.5" />}
                      {l.source}
                    </span>
                  </TableCell>
                  <TableCell className="text-[hsl(var(--lxra-fg))] whitespace-nowrap">
                    <p>{l.name}</p>
                    <p className="text-xs text-[hsl(var(--lxra-muted))]">{l.email}{l.phone ? ` · ${l.phone}` : ''}</p>
                  </TableCell>
                  <TableCell className="text-[hsl(var(--lxra-fg))] max-w-xs truncate" title={l.notes}>{l.occasion || '—'}</TableCell>
                  <TableCell className="text-[hsl(var(--lxra-muted))] text-xs capitalize">{l.service_slug || '—'}</TableCell>
                  <TableCell>
                    <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                      <SelectTrigger className={`${selectClass} w-32`} data-testid={ADMIN_LEADS.statusSelect(l.id)}>
                        <SelectValue>
                          <Badge className={`border capitalize hover:bg-transparent ${STATUS_STYLE[l.status]}`}>{l.status}</Badge>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className={contentClass}>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-[hsl(var(--lxra-muted))] whitespace-nowrap text-xs">{new Date(l.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {l.blueprint_id && (
                      <Link to={`/plan/${l.blueprint_id}`} target="_blank" data-testid={ADMIN_LEADS.blueprintLink(l.id)} className="inline-flex items-center h-8 px-2.5 rounded-lg text-xs text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))]/10 transition-colors">
                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Blueprint
                      </Link>
                    )}
                    <button type="button" onClick={() => remove(l.id)} className="inline-flex items-center h-8 w-8 justify-center rounded-lg text-[hsl(var(--lxra-muted))] hover:text-[hsl(8,55%,68%)] transition-colors" aria-label="Remove lead" data-testid={`admin-lead-delete-${l.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
